import axios from 'axios';

import createUrlWithParams from 'helpers/createUrlWithParams';

import {
  parseDsarCaseAssetForFE,
  parseDsarCaseAssetRequestTypeResultForFE,
  parseDsarCaseAssetsForFE,
  parseDsarCaseCommunicationLogForFE,
  parseDsarEligibleAssetsForFE,
  parseDsarCaseForFE,
  parseDsarCasesForFE,
} from './dsar.parsers';
import type {
  DsarCaseAsset,
  DsarCaseAssetDTO,
  DsarCaseAssetListParams,
  DsarCaseAssetStatusLogCreatePayload,
  DsarCaseCommunicationLog,
  DsarCaseCommunicationLogCreatePayload,
  DsarCaseCommunicationLogDTO,
  DsarCaseAssetRequestTypeResult,
  DsarCaseAssetRequestTypeResultDTO,
  DsarCaseAssetRequestTypeUpdatePayload,
  DsarCaseAssetUpdatePayload,
  DsarCase,
  DsarCaseCreatePayload,
  DsarCaseDTO,
  DsarCaseDistributionResult,
  DsarCaseDistributionResultDTO,
  DsarCaseDistributionPayload,
  DsarCaseListParams,
  DsarEligibleAsset,
  DsarEligibleAssetDTO,
  DsarDomainValueListParams,
  DsarDomainValueSummary,
  DsarDomainValueSummaryDTO,
  PaginatedDsarCaseAssetsResponse,
  PaginatedDsarCasesResponse,
  DsarCaseUpdatePayload,
} from './dsar.types';

const dsarCasesEndpoint = 'api/v1/dsar-cases/';
const dsarCaseAssetsEndpoint = 'api/v1/dsar-case-assets/';
const dsarDomainValuesEndpoint = 'api/v1/dsar-domain-values/';

type PaginatedDsarCasesDTOResponse = {
  count?: number;
  next?: string | null;
  previous?: string | null;
  results?: DsarCaseDTO[];
};

type PaginatedDsarCaseAssetsDTOResponse = {
  count?: number;
  next?: string | null;
  previous?: string | null;
  results?: DsarCaseAssetDTO[];
};

const parseCaseListResponse = (data: unknown): PaginatedDsarCasesResponse => {
  if (Array.isArray(data)) {
    return {
      count: data.length,
      next: null,
      previous: null,
      results: parseDsarCasesForFE(data as DsarCaseDTO[]),
    };
  }

  const paginated = data as PaginatedDsarCasesDTOResponse;
  return {
    count: paginated.count ?? 0,
    next: paginated.next ?? null,
    previous: paginated.previous ?? null,
    results: parseDsarCasesForFE(paginated.results ?? []),
  };
};

const parseCaseAssetListResponse = (data: unknown): PaginatedDsarCaseAssetsResponse => {
  if (Array.isArray(data)) {
    return {
      count: data.length,
      next: null,
      previous: null,
      results: parseDsarCaseAssetsForFE(data as DsarCaseAssetDTO[]),
    };
  }

  const paginated = data as PaginatedDsarCaseAssetsDTOResponse;
  return {
    count: paginated.count ?? 0,
    next: paginated.next ?? null,
    previous: paginated.previous ?? null,
    results: parseDsarCaseAssetsForFE(paginated.results ?? []),
  };
};

const parseDistributionResult = (data: DsarCaseDistributionResultDTO): DsarCaseDistributionResult => ({
  dsarCaseId: data.dsar_case_id,
  assetsCreated: data.assets_created,
  assetsExisting: data.assets_existing,
  requestTypeResultsCreated: data.request_type_results_created,
  requestTypeResultsExisting: data.request_type_results_existing,
  notificationPlaceholdersCreated: data.notification_placeholders_created,
});

const dsarApi = {
  getAllCases: async (params?: DsarCaseListParams): Promise<{ data: PaginatedDsarCasesResponse }> => {
    const { data } = await axios.get(createUrlWithParams(dsarCasesEndpoint, params || {}));
    return { data: parseCaseListResponse(data) };
  },

  getSingleCase: async (id: number): Promise<{ data: DsarCase }> => {
    const { data } = await axios.get(`${dsarCasesEndpoint}${id}/`);
    return { data: parseDsarCaseForFE(data as DsarCaseDTO) };
  },

  getAllCaseAssets: async (params?: DsarCaseAssetListParams): Promise<{ data: PaginatedDsarCaseAssetsResponse }> => {
    const { data } = await axios.get(createUrlWithParams(dsarCaseAssetsEndpoint, params || {}));
    return { data: parseCaseAssetListResponse(data) };
  },

  getSingleCaseAsset: async (id: number): Promise<{ data: DsarCaseAsset }> => {
    const { data } = await axios.get(`${dsarCaseAssetsEndpoint}${id}/`);
    return { data: parseDsarCaseAssetForFE(data as DsarCaseAssetDTO) };
  },

  updateCaseAsset: async (id: number, payload: DsarCaseAssetUpdatePayload): Promise<{ data: DsarCaseAsset }> => {
    const { data } = await axios.patch(`${dsarCaseAssetsEndpoint}${id}/`, payload);
    return { data: parseDsarCaseAssetForFE(data as DsarCaseAssetDTO) };
  },

  addCaseAssetStatusLog: async (id: number, payload: DsarCaseAssetStatusLogCreatePayload): Promise<{ data: DsarCaseAsset }> => {
    const { data } = await axios.post(`${dsarCaseAssetsEndpoint}${id}/status-logs/`, payload);
    return { data: parseDsarCaseAssetForFE(data as DsarCaseAssetDTO) };
  },

  updateCaseAssetRequestType: async (
    id: number,
    payload: DsarCaseAssetRequestTypeUpdatePayload,
  ): Promise<{ data: DsarCaseAssetRequestTypeResult }> => {
    const { data } = await axios.patch(`api/v1/dsar-case-asset-request-types/${id}/`, payload);
    return {
      data: parseDsarCaseAssetRequestTypeResultForFE(data as DsarCaseAssetRequestTypeResultDTO),
    };
  },

  createCase: async (payload: DsarCaseCreatePayload): Promise<{ data: DsarCase }> => {
    const { data } = await axios.post(dsarCasesEndpoint, payload);
    return { data: parseDsarCaseForFE(data as DsarCaseDTO) };
  },

  updateCase: async (id: number, payload: DsarCaseUpdatePayload): Promise<{ data: DsarCase }> => {
    const { data } = await axios.patch(`${dsarCasesEndpoint}${id}/`, payload);
    return { data: parseDsarCaseForFE(data as DsarCaseDTO) };
  },

  addCommunicationLog: async (id: number, payload: DsarCaseCommunicationLogCreatePayload): Promise<{ data: DsarCaseCommunicationLog }> => {
    const { data } = await axios.post(`${dsarCasesEndpoint}${id}/communication-logs/`, payload);
    return { data: parseDsarCaseCommunicationLogForFE(data as DsarCaseCommunicationLogDTO) };
  },

  deleteCase: async (id: number): Promise<void> => {
    await axios.delete(`${dsarCasesEndpoint}${id}/`);
  },

  distributeCase: async (id: number): Promise<{ data: DsarCaseDistributionResult }> => {
    const { data } = await axios.post(`${dsarCasesEndpoint}${id}/distribute/`);
    return { data: parseDistributionResult(data as DsarCaseDistributionResultDTO) };
  },

  getEligibleAssets: async (id: number): Promise<{ data: DsarEligibleAsset[] }> => {
    const { data } = await axios.get(`${dsarCasesEndpoint}${id}/eligible-assets/`);
    return { data: parseDsarEligibleAssetsForFE(data as DsarEligibleAssetDTO[]) };
  },

  distributeCaseToAssets: async (id: number, payload: DsarCaseDistributionPayload): Promise<{ data: DsarCaseDistributionResult }> => {
    const { data } = await axios.post(`${dsarCasesEndpoint}${id}/distribute/`, payload);
    return { data: parseDistributionResult(data as DsarCaseDistributionResultDTO) };
  },

  getDomainValues: async (params?: DsarDomainValueListParams): Promise<{ data: DsarDomainValueSummary[] }> => {
    const { data } = await axios.get(createUrlWithParams(dsarDomainValuesEndpoint, params || {}));
    return {
      data: (data as DsarDomainValueSummaryDTO[]).map(value => ({
        id: value.id,
        group: value.group,
        code: value.code,
        name: value.name,
        displayOrder: value.display_order,
      })),
    };
  },
};

export default dsarApi;
